import type { Hole, SignatureHole, Stat } from '@/types'
import { media } from '@/constants/media'

/**
 * The championship routing. Coordinates live in the course map's
 * 1000 x 640 drawing space: tee, one curve control point, green.
 */
export const holes: Hole[] = [
    {
        number: 1,
        name: 'Threshold',
        par: 4,
        yards: 402,
        strokeIndex: 9,
        description: 'A wide, welcoming fairway that falls toward the heath. Hold the left side for the best look at a green open at the front.',
        tee: [468, 522],
        control: [405, 548],
        green: [338, 552]
    },
    {
        number: 2,
        name: 'Pine Gate',
        par: 4,
        yards: 438,
        strokeIndex: 3,
        description: 'The drive threads a gap between two stands of umbrella pine. Anything right catches the slope and runs into the needles.',
        tee: [318, 540],
        control: [262, 532],
        green: [196, 486]
    },
    {
        number: 3,
        name: 'Salt Wind',
        par: 3,
        yards: 176,
        strokeIndex: 17,
        description: 'Your first sight of the ocean, played straight into the prevailing north-westerly. Take one more club than you think.',
        tee: [178, 468],
        control: [158, 452],
        green: [132, 430]
    },
    {
        number: 4,
        name: 'The Pines',
        par: 4,
        yards: 412,
        strokeIndex: 7,
        description: 'A dogleg left around two-hundred-year-old pines. The brave line leaves a wedge; the safe line leaves a long iron to a shelf green.',
        tee: [142, 408],
        control: [112, 338],
        green: [186, 282]
    },
    {
        number: 5,
        name: 'Long Acre',
        par: 5,
        yards: 552,
        strokeIndex: 11,
        description: 'A split fairway divided by a ridge of native grass. The high road opens the green in two; the low road is longer but kinder.',
        tee: [204, 262],
        control: [196, 170],
        green: [296, 108]
    },
    {
        number: 6,
        name: 'Heron',
        par: 4,
        yards: 371,
        strokeIndex: 13,
        description: 'Played along the reed beds of the north lake, where herons wait on the far bank. Precision off the tee beats power.',
        tee: [318, 98],
        control: [376, 92],
        green: [428, 148]
    },
    {
        number: 7,
        name: 'Quarry',
        par: 3,
        yards: 187,
        strokeIndex: 15,
        description: 'Over the old stone quarry to a green framed by the cut rock face. There is nowhere to miss, and nothing else like it.',
        tee: [440, 170],
        control: [456, 196],
        green: [470, 226]
    },
    {
        number: 8,
        name: 'Ridge',
        par: 4,
        yards: 456,
        strokeIndex: 1,
        description: 'The hardest hole on the card, climbing the spine of the estate. A par here is worth celebrating on the 9th tee.',
        tee: [456, 248],
        control: [398, 262],
        green: [344, 340]
    },
    {
        number: 9,
        name: 'Homeward',
        par: 5,
        yards: 534,
        strokeIndex: 5,
        description: 'Turns back toward the clubhouse with the whole front nine laid out below you. The green sits just beneath the terrace.',
        tee: [356, 364],
        control: [372, 470],
        green: [478, 478]
    },
    {
        number: 10,
        name: 'Outlook',
        par: 4,
        yards: 418,
        strokeIndex: 10,
        description: 'From the tee you can see six kilometres of coastline. The fairway is wider than it looks, and the wind is stronger than it feels.',
        tee: [538, 468],
        control: [600, 452],
        green: [640, 376]
    },
    {
        number: 11,
        name: 'Long Heath',
        par: 5,
        yards: 568,
        strokeIndex: 4,
        description: 'The longest hole at Morrow runs through open heath in flower from March to June. Three good shots and a patient putter.',
        tee: [662, 360],
        control: [762, 382],
        green: [842, 298]
    },
    {
        number: 12,
        name: 'Cork Oak',
        par: 4,
        yards: 389,
        strokeIndex: 14,
        description: 'A single cork oak stands in the middle of the fairway. Left, right or over the top, every line has its reward.',
        tee: [858, 280],
        control: [872, 212],
        green: [822, 154]
    },
    {
        number: 13,
        name: 'Dune Walk',
        par: 3,
        yards: 205,
        strokeIndex: 12,
        description: 'An elevated green set into the dunes, with sand on every side except the one you need.',
        tee: [800, 140],
        control: [770, 122],
        green: [736, 120]
    },
    {
        number: 14,
        name: 'Driftline',
        par: 4,
        yards: 447,
        strokeIndex: 2,
        description: 'Follows the ridge where the fairway rolls like the sea beyond it. The wind decides how long it plays.',
        tee: [714, 124],
        control: [648, 128],
        green: [602, 208]
    },
    {
        number: 15,
        name: 'Short Straw',
        par: 4,
        yards: 327,
        strokeIndex: 18,
        description: 'A drivable par four with a green the size of a living room. Lay up and trust your wedge, or go for it.',
        tee: [606, 234],
        control: [648, 238],
        green: [690, 292]
    },
    {
        number: 16,
        name: 'Mirror',
        par: 3,
        yards: 164,
        strokeIndex: 16,
        description: 'A peninsula green on the reservoir, water on three sides. On still mornings the flag appears twice.',
        tee: [722, 402],
        control: [744, 416],
        green: [772, 446]
    },
    {
        number: 17,
        name: 'Long Light',
        par: 5,
        yards: 541,
        strokeIndex: 8,
        description: 'Played west into the evening sun. Late tee times finish their round in gold.',
        tee: [814, 500],
        control: [748, 560],
        green: [640, 540]
    },
    {
        number: 18,
        name: 'The Return',
        par: 4,
        yards: 461,
        strokeIndex: 6,
        description: 'Uphill to the clubhouse, with the terrace watching every approach. The finest walk home we know.',
        tee: [662, 578],
        control: [600, 590],
        green: [518, 506]
    }
]

export const frontNine = holes.slice(0, 9)
export const backNine = holes.slice(9)

const sum = (list: Hole[], key: 'par' | 'yards'): number => list.reduce((total, hole) => total + hole[key], 0)

export const courseTotals = {
    outPar: sum(frontNine, 'par'),
    outYards: sum(frontNine, 'yards'),
    inPar: sum(backNine, 'par'),
    inYards: sum(backNine, 'yards'),
    par: sum(holes, 'par'),
    yards: sum(holes, 'yards')
}

export const clubhousePosition = { x: 505, y: 548 }

export const signatureHoles: SignatureHole[] = [
    {
        number: 4,
        name: 'The Pines',
        par: 4,
        yards: 412,
        strokeIndex: 7,
        description: 'A dogleg left that bends around a stand of two-hundred-year-old umbrella pines. Take them on and a wedge remains; play away and a long iron finds a shelf green tucked into the forest edge.',
        detail: 'Carry over the corner: 238 yds',
        image: media.holePines
    },
    {
        number: 7,
        name: 'Quarry',
        par: 3,
        yards: 187,
        strokeIndex: 15,
        description: 'The tee sits on the lip of a quarry that supplied stone to the region for three centuries. The green is framed by the rock face itself, so a missed shot is simply gone.',
        detail: 'Carry to the front: 164 yds',
        image: media.holeQuarry
    },
    {
        number: 11,
        name: 'Long Heath',
        par: 5,
        yards: 568,
        strokeIndex: 4,
        description: 'The longest hole on the estate crosses open heathland in flower from March to June. Three disciplined shots, a patient putter, and the ocean in view the whole way.',
        detail: 'Heath in flower: March to June',
        image: media.holeHeath
    },
    {
        number: 16,
        name: 'Mirror',
        par: 3,
        yards: 164,
        strokeIndex: 16,
        description: 'A peninsula green reaching into the reservoir with water on three sides. On still mornings the flag appears twice, once in the air and once in the water.',
        detail: 'Green depth: 28 yds',
        image: media.holeMirror
    },
    {
        number: 18,
        name: 'The Return',
        par: 4,
        yards: 461,
        strokeIndex: 6,
        description: 'Uphill toward the clubhouse, where the terrace watches every approach. A generous fairway and a fiercely contoured green make it a hole of nerve.',
        detail: 'Climbs 21 m to the green',
        image: media.holeReturn
    }
]

export const landscapeStats: Stat[] = [
    { value: 240, label: 'hectares of coastal estate' },
    { value: 18, label: 'holes on the championship course, par 72' },
    { value: 7048, label: 'yards from the back tees' },
    { value: 63, suffix: '%', label: 'of the estate left as native habitat' }
]
